import { Component, AfterViewInit, EventEmitter, ElementRef, forwardRef, Input, Output, ViewChild } from '@angular/core';
import { ControlValueAccessor, NgControl, NG_VALUE_ACCESSOR, Validator, FormControl, NG_VALIDATORS } from '@angular/forms';

import { toFixed } from '../../../core/services/utils/numbers';

@Component({
    selector: 'sgt-decimal',
    templateUrl: 'decimal.component.html',
    providers: [
        { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => DecimalComponent), multi: true },
        { provide: NG_VALIDATORS, useExisting: forwardRef(() => DecimalComponent), multi: true }
    ]
})
export class DecimalComponent implements AfterViewInit, ControlValueAccessor, Validator {
    @Input() ngModel: any;
    @Output() ngModelChange = new EventEmitter<number>();

    @Input() minIntegerDigits = 1; // Número mínimo de dígitos antes da vírgula
    @Input() minDecimalDigits = 2; // Número mínimo de dígitos após a vírgula
    @Input() maxDecimalDigits = 2; // Número máximo de dígitos após a vírgula
    @Input() min: number; // Valor mínimo permitido
    @Input() max: number; // Valor máximo permitido
    @Input() required: any;
    @Input() disabled: any;
    @Input() readonly: any;
    @Input() ngControl: NgControl;
    @Input() autofocus = false;
    /** Ao focar no input */
    @Output() focus = new EventEmitter<FocusEvent>();
    /** Ao perder o foco no input */
    @Output() focusout = new EventEmitter<FocusEvent>();
    @Input() placeholder = '';

    @ViewChild('inputDecimal') inputDecimal: ElementRef;
    inputDecimalNativeElement: any;

    propagateChange: any = () => {};

    constructor(private el: ElementRef) { }


    ngAfterViewInit() {
        this.inputDecimalNativeElement = this.inputDecimal.nativeElement;

        if (this.autofocus) {
            jQuery(this.inputDecimalNativeElement).focus();
        }
    }

    /** obtem o valor no tipo number através do valor digitado */
    private getNumber(value: string): number {
        value = value.replace(/\./g, ''); // Removendo o "."
        value = value.replace(/\,/g, '.'); // Removendo a ","
        return Number(value);
    }

    /** Validando se o valor digitado no input é um number válido */
    private validar(value: string): boolean {
        if (!value || value.trim().length === 0) {
            return true;
        }

        const decimalValue = this.getNumber(value);
        return decimalValue !== undefined && decimalValue !== null && !isNaN(decimalValue);
    }

    /** Valida se o valor é maior ou igual ao valor mínimo permitido */
    private validarValorMinimo(value: number): boolean {
        if (!this.min) {
            return true;
        }

        return value >= this.min;
    }

    /** Valida se o valor é menor ou igual ao valor máximo permitido */
    private validarValorMaximo(value: number): boolean {
        if (!this.max) {
            return true;
        }

        return value <= this.max;
    }

     /** Realiza a validação do FormControl de decimal */
     validate(c: FormControl) {
        if (!this.inputDecimalNativeElement) {
            return null;
        }

        // Validando se valor informado é um number
        const value = this.inputDecimalNativeElement.value;
        if (!value) {
            return null;
        }
        const isValid = this.validar(value);
        if (!isValid) {
            return {
                decimalInvalid: {
                    valid: false
                }
            }
        }

        const numerValue = this.getNumber(value);
        // Validando valor mínimo
        const isMinValid = this.validarValorMinimo(numerValue);
        if (!isMinValid) {
            return {
                min: {
                    valid: false
                }
            }
        }
        // Validando valor máximo
        const isMaxValid = this.validarValorMaximo(numerValue);
        if (!isMaxValid) {
            return {
                max: {
                    valid: false
                }
            }
        }

        return null;
    }

    /** Formata o valor em uma string no padrão "pt-br" */
    private formatToString(value: number): string {
        if (value === undefined || value === null || isNaN(value)) {
            return null;
        }
        if (typeof value !== 'number') {
            value = Number(value);
        }

        return value.toLocaleString('pt-br', {style: 'decimal',
                                              minimumIntegerDigits: this.minIntegerDigits,
                                              minimumFractionDigits: this.minDecimalDigits,
                                              maximumFractionDigits: this.maxDecimalDigits});
    }

    /** Formata um valor dígitado em um number válido */
    private formatToNumber(value: string): number {
        if (!value || value === '') {
            return null;
        }
        value = value.replace(/\./g, ''); // // Troca "." por ""
        value = value.replace(/\,/g, '.'); // Troca "," por "." pois o separador de decimal do number é o "."

        const decimalValue = Number(value);
        if (decimalValue !== undefined && decimalValue !== null && !isNaN(decimalValue)) {
            // return decimalValue;
            return toFixed(decimalValue, this.maxDecimalDigits);
        } else {
            return null;
        }
    }

    /** Seta o foco no input */
    setFocus() {
        jQuery(this.inputDecimalNativeElement).focus();
    }

    /** Ao focar no input */
    onFocus(event: FocusEvent) {
        this.focus.emit(event);
    }

    /** Ao perder o foco no input */
    onFocusout(event: FocusEvent) {
        this.focusout.emit(event);
    }

    /** Ao digitar no input */
    onKeyup(e: KeyboardEvent) {
        // Ignorando a tecla "tab" ao entrar no input
        if (e.keyCode === 9) {
            return;
        }

        // Formatando o valor do input para um number e propagando a alteração para o ngModel
        const value = this.inputDecimalNativeElement.value;
        const decimalValue = this.formatToNumber(value);
        this.propagateChange(decimalValue);
    }

    /** Ao sair do input */
    onBlur() {
        // Formatando o valor digitado no input
        const value = this.inputDecimalNativeElement.value;
        const decimalValue = this.formatToNumber(value);
        this.inputDecimalNativeElement.value = this.formatToString(decimalValue);
        this.ngControl.control.updateValueAndValidity();
    }

    /** Quando recebe um valor */
    writeValue(value: number) {
        if (!this.inputDecimalNativeElement) {
            return;
        }

        if (value !== undefined && value !== null) {
            this.inputDecimalNativeElement.value = this.formatToString(value);
        } else {
            this.inputDecimalNativeElement.value = '';
        }
    }

    /** Propaga as alterações realizadas no input */
    registerOnChange(fn) {
        this.propagateChange = fn;
    }

    registerOnTouched() { }
}
